import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, X, Edit2, Trash2, Save, Send, Mail, Star, Inbox, MailCheck, Copy, Check, RefreshCw, FileCode } from 'lucide-react';

const EMPTY = {
  label: '',
  senderName: '',
  senderEmail: '',
  smtpHost: 'mail.your-server.de',
  smtpPort: 587,
  smtpUser: '',
  smtpPassword: '',
  security: 'starttls',
  signatureHtml: '',
  isDefault: false,
  replyTo: '',
};

// ─────────────── Signature HTML-File Upload ───────────────
//
// Lets the user pick a `.html` / `.htm` / `.txt` file from disk; FileReader
// reads it as text and we hand the content to the parent textarea so it stays
// editable. We strip <script>…</script> + inline `on*=` handlers as a
// defensive XSS-sanitise pass (the content is rendered server-side into
// outgoing mails and client-side via dangerouslySetInnerHTML for preview).
function SignatureFileUpload({ onLoaded }) {
  const ref = useRef(null);
  const [msg, setMsg] = useState(null);

  const clean = (html) => html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    if (!(name.endsWith('.html') || name.endsWith('.htm') || name.endsWith('.txt'))) {
      setMsg({ type: 'error', text: 'Nur .html, .htm oder .txt erlaubt' });
      return;
    }
    if (f.size > 512 * 1024) {
      setMsg({ type: 'error', text: 'Datei > 512 KB — bitte kompakter halten' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      const cleaned = clean(raw);
      const removed = raw.length - cleaned.length;
      onLoaded(cleaned);
      setMsg({ type: 'success', text: `Geladen: ${f.name}${removed > 0 ? ` · ${removed} Zeichen bereinigt` : ''}` });
      if (ref.current) ref.current.value = '';
    };
    reader.onerror = () => setMsg({ type: 'error', text: 'Datei konnte nicht gelesen werden' });
    reader.readAsText(f, 'utf-8');
  };

  return (
    <div className="flex items-center gap-2" data-testid="sig-file-upload">
      <input ref={ref} type="file" accept=".html,.htm,.txt" className="hidden"
             onChange={onFile} data-testid="sig-file-input" />
      <button type="button" onClick={() => ref.current?.click()}
              className="text-[11px] text-[#9ec043] hover:text-[#8aad38] flex items-center gap-1 underline-offset-2 hover:underline"
              data-testid="btn-sig-file">
        <FileCode className="w-3 h-3" /> Signatur-Datei laden (.html)
      </button>
      {msg && (
        <span className={`text-[10px] ${msg.type === 'success' ? 'text-green-600' : 'text-red-500'}`} data-testid="sig-file-msg">
          {msg.text}
        </span>
      )}
    </div>
  );
}

export default function MailSettingsTab() {
  const [sub, setSub] = useState('out');
  return (
    <div>
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        <button onClick={() => setSub('out')}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${sub === 'out' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400'}`}
                data-testid="subtab-mail-out">
          <MailCheck className="w-3.5 h-3.5 inline mr-1" /> Postausgang (SMTP)
        </button>
        <button onClick={() => setSub('routing')}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${sub === 'routing' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400'}`}
                data-testid="subtab-mail-routing">
          <MailCheck className="w-3.5 h-3.5 inline mr-1" /> Routing (Action → Absender)
        </button>
        <button onClick={() => setSub('in')}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${sub === 'in' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400'}`}
                data-testid="subtab-mail-in">
          <Inbox className="w-3.5 h-3.5 inline mr-1" /> Posteingang
        </button>
      </div>
      {sub === 'out' ? <OutgoingSection /> : sub === 'routing' ? <RoutingSection /> : <IncomingSection />}
    </div>
  );
}

function RoutingSection() {
  const [routing, setRouting] = useState([]);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [rRouting, rSenders] = await Promise.all([
      authFetch('/api/mail/routing'),
      authFetch('/api/mail/settings'),
    ]);
    if (rRouting.ok) setRouting((await rRouting.json()).routing || []);
    if (rSenders.ok) setSenders(await rSenders.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateMapping = async (actionKey, settingId) => {
    setSaving(actionKey);
    const r = await authFetch(`/api/mail/routing/${actionKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settingId: settingId || null }),
    });
    if (r.ok) {
      setRouting(prev => prev.map(x => x.key === actionKey ? { ...x, settingId: settingId || null } : x));
    } else {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || 'Fehler beim Speichern');
    }
    setSaving(null);
  };

  const defaultSender = senders.find(s => s.isDefault && s.active !== false);

  if (loading) return <div className="text-sm text-gray-400">Lade Routing-Konfiguration …</div>;

  return (
    <div data-testid="mail-routing-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
        <strong>Was ist Routing?</strong> Jede ausgehende Aktion (Ticket-Antwort, Auftragsbestätigung, Kalender-Erinnerung …)
        kann eine eigene Absender-Adresse nutzen. Wenn keine zugewiesen ist, fällt das System
        auf die als <em>Default</em> markierte SMTP-Identität zurück
        {defaultSender ? <> — aktuell: <strong>{defaultSender.senderEmail}</strong></> : <> (aktuell keine als Default markiert!)</>}.
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Aktion</th>
              <th className="text-left px-4 py-2">Empfohlene Adresse</th>
              <th className="text-left px-4 py-2">Absender</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {routing.map(row => (
              <tr key={row.key} data-testid={`routing-row-${row.key}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#32373c]">{row.label}</div>
                  <div className="text-[10px] text-gray-400 font-mono">{row.key}</div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">{row.default_hint || '—'}</td>
                <td className="px-4 py-3">
                  <select
                    value={row.settingId || ''}
                    onChange={(e) => updateMapping(row.key, e.target.value)}
                    disabled={saving === row.key}
                    className="w-full max-w-md h-9 border border-gray-200 rounded px-2 text-sm bg-white"
                    data-testid={`routing-select-${row.key}`}
                  >
                    <option value="">— Default verwenden —</option>
                    {senders.map(s => (
                      <option key={s.id} value={s.id} disabled={s.active === false}>
                        {s.label} ({s.senderEmail}){s.active === false ? ' — deaktiviert' : ''}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IncomingSection() {
  const [endpoints, setEndpoints] = useState([]);
  const [label, setLabel] = useState('');
  const [role, setRole] = useState('support');
  const [copiedId, setCopiedId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    const res = await authFetch('/api/mail-inbound/endpoints');
    if (res.ok) setEndpoints(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!label.trim()) { setMessage({ type: 'error', text: 'Bezeichnung erforderlich' }); return; }
    const res = await authFetch('/api/mail-inbound/endpoints', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, role }),
    });
    if (res.ok) { setLabel(''); setMessage({ type: 'success', text: 'Ingest-Endpoint angelegt.' }); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm('Endpoint löschen? Der Forward-Link funktioniert danach nicht mehr.')) return;
    await authFetch(`/api/mail-inbound/endpoints/${id}`, { method: 'DELETE' });
    load();
  };

  const rotate = async (id) => {
    if (!window.confirm('Secret rotieren? Der alte Link wird ungültig.')) return;
    await authFetch(`/api/mail-inbound/endpoints/${id}/rotate-secret`, { method: 'POST' });
    load();
  };

  const copy = async (e) => {
    try { await navigator.clipboard.writeText(e.ingestUrl); setCopiedId(e.id); setTimeout(() => setCopiedId(null), 2000); } catch { /* */ }
  };

  return (
    <div data-testid="mail-incoming">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
        <strong>Forward-Push (empfohlen):</strong> Lege in deinem Hetzner-Postfach eine Weiterleitung an, die eingehende Mails (z. B. an <code>support@</code>) als
        POST-Request an die unten generierte Ingest-URL schickt. Das System klassifiziert automatisch, legt Tickets/Anfragen an und verschickt Auto-Replies mit Ticket-Nummer.
        Folge-Mails werden anhand der Ticket-ID im Betreff automatisch zugeordnet.
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h3 className="text-sm font-semibold text-[#32373c] mb-3">Neuen Ingest-Endpoint anlegen</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-gray-500">Bezeichnung</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)}
                   placeholder="z. B. Support-Postfach, Bestellungen"
                   className="mt-1 h-9 text-sm" data-testid="mailin-label" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Routing</Label>
            <select value={role} onChange={e => setRole(e.target.value)}
                    className="mt-1 h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="mailin-role">
              <option value="support">Support → Ticket + Auto-Reply</option>
              <option value="order">Bestellungen → Produktanfrage/Bestellanfrage</option>
              <option value="general">Allgemein → Klassifizierung via KI</option>
            </select>
          </div>
          <Button onClick={create} className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-create-mailin">
            <Plus className="w-3.5 h-3.5 mr-1" /> Anlegen
          </Button>
        </div>
      </div>

      {message && (
        <div className={`rounded p-3 mb-4 text-sm border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-2 mb-6">
        {endpoints.length === 0 && (
          <p className="text-xs text-gray-400">Noch keine Ingest-Endpoints angelegt.</p>
        )}
        {endpoints.map(e => (
          <div key={e.id} className="bg-white rounded-lg border border-gray-200 p-3" data-testid={`mailin-row-${e.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-medium text-[#32373c]">{e.label}</span>
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#9ec043]/10 text-[#9ec043] uppercase">{e.role}</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => rotate(e.id)} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" title="Secret neu generieren" data-testid={`btn-rotate-${e.id}`}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(e.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Löschen" data-testid={`btn-del-mailin-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-xs font-mono text-[#32373c] break-all" data-testid={`ingest-url-${e.id}`}>
                {e.ingestUrl}
              </code>
              <Button variant="outline" size="sm" onClick={() => copy(e)} className="h-8 text-xs shrink-0" data-testid={`btn-copy-ingest-${e.id}`}>
                {copiedId === e.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              POST-Body: JSON mit <code>fromEmail, subject, bodyText</code> (+ optional <code>fromName, bodyHtml, messageId, inReplyTo</code>).
            </p>
          </div>
        ))}
      </div>

      <ImapSection />
    </div>
  );
}

const IMAP_EMPTY = {
  label: '', host: '', port: 993, security: 'ssl',
  user: '', password: '',
  folders: 'INBOX', role: 'support',
  enabled: true, pollIntervalMinutes: 3, replySettingId: '',
};

function ImapSection() {
  const [items, setItems] = useState([]);
  const [smtpSenders, setSmtpSenders] = useState([]);
  const [form, setForm] = useState(IMAP_EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      authFetch('/api/mail-inbound/imap'),
      authFetch('/api/mail/settings'),
    ]);
    if (a.ok) setItems(await a.json());
    if (b.ok) setSmtpSenders(await b.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.host || !form.user) {
      setMsg({ type: 'error', text: 'Host und Benutzer erforderlich' }); return;
    }
    if (!editingId && !form.password) {
      setMsg({ type: 'error', text: 'Passwort erforderlich' }); return;
    }
    const body = {
      ...form,
      folders: (form.folders || 'INBOX').split(',').map(s => s.trim()).filter(Boolean),
      port: Number(form.port) || 993,
      pollIntervalMinutes: Number(form.pollIntervalMinutes) || 3,
      replySettingId: form.replySettingId || null,
    };
    const url = editingId
      ? `/api/mail-inbound/imap/${editingId}`
      : '/api/mail-inbound/imap';
    const res = await authFetch(url, {
      method: editingId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setMsg({ type: 'success', text: editingId ? 'IMAP-Konto aktualisiert' : 'IMAP-Konto angelegt' });
      setForm(IMAP_EMPTY); setEditingId(null); setShowForm(false); load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg({ type: 'error', text: d.detail || 'Fehler beim Speichern' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('IMAP-Konto löschen?')) return;
    await authFetch(`/api/mail-inbound/imap/${id}`, { method: 'DELETE' });
    load();
  };

  const test = async (id) => {
    setBusyId(id); setMsg(null);
    const res = await authFetch(`/api/mail-inbound/imap/${id}/test`, { method: 'POST' });
    setBusyId(null);
    if (res.ok) {
      const d = await res.json();
      setMsg({ type: 'success', text: `Login OK · ${d.folders?.length || 0} Ordner erreichbar` });
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg({ type: 'error', text: d.detail || 'Login fehlgeschlagen' });
    }
  };

  const pollNow = async (id) => {
    setBusyId(id); setMsg(null);
    const res = await authFetch(`/api/mail-inbound/imap/${id}/poll-now`, { method: 'POST' });
    setBusyId(null);
    if (res.ok) {
      const d = await res.json();
      setMsg({ type: 'success', text: `Gepollt · ${d.fetched || 0} neue Mails · ${d.processed || 0} verarbeitet` });
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg({ type: 'error', text: d.detail || 'Poll fehlgeschlagen' });
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      label: it.label || '', host: it.host || '', port: it.port || 993,
      security: it.security || 'ssl', user: it.user || '', password: '',
      folders: (it.folders || ['INBOX']).join(','),
      role: it.role || 'support',
      enabled: !!it.enabled,
      pollIntervalMinutes: it.pollIntervalMinutes || 3,
      replySettingId: it.replySettingId || '',
    });
    setShowForm(true);
  };

  return (
    <div className="mt-6" data-testid="imap-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Inbox className="w-4 h-4 text-[#9ec043]" /> IMAP-Pull (direkter Postfach-Abruf)
        </h3>
        {!showForm && (
          <Button onClick={() => { setEditingId(null); setForm(IMAP_EMPTY); setShowForm(true); }}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-8 text-xs" data-testid="btn-new-imap">
            <Plus className="w-3.5 h-3.5 mr-1" /> Konto hinzufügen
          </Button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs text-blue-800">
        Das System pollt aktive Konten alle paar Minuten, zieht neue (UNSEEN) Mails, klassifiziert via KI und legt
        <strong> Tickets</strong> (Rolle = support) oder <strong>Anfragen</strong> (order/general) an. Mails werden
        nach dem Abruf als gelesen markiert und per Message-ID entdupliziert.
      </div>

      {msg && (
        <div className={`rounded p-3 mb-3 text-sm border ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3" data-testid="imap-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Bezeichnung</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                     placeholder="z. B. support@ees" className="h-9 text-sm mt-1" data-testid="imap-label" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Routing</Label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                      className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="imap-role">
                <option value="support">Support → Ticket</option>
                <option value="order">Bestellungen → Anfrage</option>
                <option value="general">Allgemein → KI-Klassifizierung</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">IMAP-Host</Label>
              <Input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })}
                     placeholder="imap.your-server.de" className="h-9 text-sm mt-1" data-testid="imap-host" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-500">Port</Label>
                <Input type="number" value={form.port} onChange={e => setForm({ ...form, port: e.target.value })}
                       className="h-9 text-sm mt-1" data-testid="imap-port" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Verschlüsselung</Label>
                <select value={form.security} onChange={e => setForm({ ...form, security: e.target.value })}
                        className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="imap-security">
                  <option value="ssl">SSL (993)</option>
                  <option value="starttls">STARTTLS (143)</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Benutzer</Label>
              <Input value={form.user} onChange={e => setForm({ ...form, user: e.target.value })}
                     placeholder="support@ees-energiespeicher.de" className="h-9 text-sm mt-1" data-testid="imap-user" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">
                Passwort {editingId && <span className="text-gray-400">(leer = unverändert)</span>}
              </Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                     className="h-9 text-sm mt-1" data-testid="imap-password" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Ordner (Komma-separiert)</Label>
              <Input value={form.folders} onChange={e => setForm({ ...form, folders: e.target.value })}
                     placeholder="INBOX" className="h-9 text-sm mt-1" data-testid="imap-folders" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-500">Abruf-Intervall (Min.)</Label>
                <Input type="number" min="1" value={form.pollIntervalMinutes}
                       onChange={e => setForm({ ...form, pollIntervalMinutes: e.target.value })}
                       className="h-9 text-sm mt-1" data-testid="imap-interval" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                  <input type="checkbox" checked={form.enabled}
                         onChange={e => setForm({ ...form, enabled: e.target.checked })}
                         data-testid="imap-enabled" />
                  Abruf aktiv
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Auto-Reply-Absender (für Support-Tickets)</Label>
              <select value={form.replySettingId || ''}
                      onChange={e => setForm({ ...form, replySettingId: e.target.value })}
                      className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="imap-reply-setting">
                <option value="">— Standard-Absender verwenden —</option>
                {smtpSenders.map(s => <option key={s.id} value={s.id}>{s.label} ({s.senderEmail})</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={submit} className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-save-imap">
              <Save className="w-3.5 h-3.5 mr-1" /> {editingId ? 'Aktualisieren' : 'Anlegen'}
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setForm(IMAP_EMPTY); }}
                    className="h-9 text-sm" data-testid="btn-cancel-imap">Abbrechen</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && !showForm && (
          <p className="text-xs text-gray-400">Noch keine IMAP-Konten konfiguriert.</p>
        )}
        {items.map(it => (
          <div key={it.id} className="bg-white rounded-lg border border-gray-200 p-3" data-testid={`imap-row-${it.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#32373c]">{it.label}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9ec043]/10 text-[#9ec043] uppercase">{it.role}</span>
                  {it.enabled ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">aktiv · alle {it.pollIntervalMinutes} Min.</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">pausiert</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5 font-mono truncate">
                  {it.user}@{it.host}:{it.port} ({it.security}) · {(it.folders || []).join(', ')}
                </div>
                {it.lastPollAt && (
                  <div className="text-[10px] text-gray-400">
                    Letzter Abruf: {new Date(it.lastPollAt).toLocaleString('de-DE')}
                    {it.lastError && <span className="text-red-500"> · Fehler: {it.lastError}</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={() => test(it.id)} disabled={busyId === it.id}
                        className="h-8 text-xs" data-testid={`btn-test-imap-${it.id}`}>
                  {busyId === it.id ? 'Teste…' : 'Login testen'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => pollNow(it.id)} disabled={busyId === it.id}
                        className="h-8 text-xs" data-testid={`btn-poll-imap-${it.id}`}>
                  Jetzt abrufen
                </Button>
                <button onClick={() => startEdit(it)} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" title="Bearbeiten" data-testid={`btn-edit-imap-${it.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(it.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Löschen" data-testid={`btn-del-imap-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutgoingSection() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(null); // setting id
  const [testTo, setTestTo] = useState('');

  const load = useCallback(async () => {
    const res = await authFetch('/api/mail/settings');
    if (res.ok) setItems(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const openNew = () => { resetForm(); setShowForm(true); };

  const openEdit = (it) => {
    setForm({
      label: it.label || '',
      senderName: it.senderName || '',
      senderEmail: it.senderEmail || '',
      smtpHost: it.smtpHost || '',
      smtpPort: it.smtpPort || 587,
      smtpUser: it.smtpUser || '',
      smtpPassword: '', // blank — only overwrite if user types
      security: it.security || 'starttls',
      signatureHtml: it.signatureHtml || '',
      isDefault: !!it.isDefault,
      replyTo: it.replyTo || '',
    });
    setEditingId(it.id);
    setShowForm(true);
    setError('');
  };

  const save = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (!form.label || !form.senderEmail || !form.smtpHost || !form.smtpUser) {
        setError('Bitte alle Pflichtfelder ausfüllen (Bezeichnung, Absender, Host, User).');
        return;
      }
      if (!editingId && !form.smtpPassword) {
        setError('Beim Anlegen ist das SMTP-Passwort Pflicht.');
        return;
      }
      const payload = { ...form, smtpPort: Number(form.smtpPort) };
      if (editingId && !payload.smtpPassword) delete payload.smtpPassword;
      if (!payload.replyTo) delete payload.replyTo;
      const url = editingId ? `/api/mail/settings/${editingId}` : '/api/mail/settings';
      const method = editingId ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Speichern fehlgeschlagen');
        return;
      }
      setSuccess(editingId ? 'Konfiguration aktualisiert' : 'Konfiguration angelegt');
      resetForm();
      load();
    } finally {
      setLoading(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Mailserver "${it.label}" wirklich löschen?`)) return;
    setError(''); setSuccess('');
    const res = await authFetch(`/api/mail/settings/${it.id}`, { method: 'DELETE' });
    if (res.ok) { setSuccess(`"${it.label}" gelöscht`); load(); }
    else { const d = await res.json().catch(()=>({})); setError(d.detail || 'Fehler'); }
  };

  const toggleActive = async (it) => {
    setError(''); setSuccess('');
    const next = it.active === false;
    const res = await authFetch(`/api/mail/settings/${it.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: next }),
    });
    if (res.ok) { setSuccess(`"${it.label}" ${next ? 'aktiviert' : 'pausiert'}`); load(); }
    else { const d = await res.json().catch(()=>({})); setError(d.detail || 'Fehler'); }
  };

  const sendTest = async (id) => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await authFetch(`/api/mail/settings/${id}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testTo }),
      });
      if (res.ok) {
        setSuccess(`Testmail an ${testTo} versendet.`);
        setTestOpen(null); setTestTo('');
      } else {
        const d = await res.json().catch(()=>({}));
        setError(d.detail || 'Testmail fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const bind = (field) => ({
    value: form[field] ?? '',
    onChange: (e) => setForm(p => ({ ...p, [field]: e.target.value })),
  });

  return (
    <div data-testid="mail-settings-tab">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#9ec043]" /> Mailserver ({items.length})
        </h2>
        {!showForm && (
          <Button onClick={openNew} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-mail">
            <Plus className="w-4 h-4 mr-1" /> Absender anlegen
          </Button>
        )}
      </div>

      {success && <div className="text-sm text-green-600 bg-green-50 rounded p-3 mb-3 border border-green-200" data-testid="mail-success">{success}</div>}
      {error && <div className="text-sm text-red-600 bg-red-50 rounded p-3 mb-3 border border-red-200" data-testid="mail-error">{error}</div>}

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm" data-testid="mail-form">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-[#32373c]">
              {editingId ? 'Absender bearbeiten' : 'Neuer SMTP-Absender (z. B. Hetzner)'}
            </h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs text-gray-500">Bezeichnung</Label>
              <Input {...bind('label')} placeholder="z. B. Support, Vertrieb" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-label" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Absender-Name</Label>
              <Input {...bind('senderName')} placeholder="EES Support" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-sender-name" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Absender-E-Mail</Label>
              <Input type="email" {...bind('senderEmail')} placeholder="support@ees-energiespeicher.de" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-sender-email" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Reply-To (optional)</Label>
              <Input type="email" {...bind('replyTo')} placeholder="Leer = gleiche wie Absender" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-reply-to" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">SMTP-Server</Label>
              <Input {...bind('smtpHost')} placeholder="mail.your-server.de (Hetzner)" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-host" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Port</Label>
                <Input type="number" {...bind('smtpPort')} className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-port" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Verschlüsselung</Label>
                <select value={form.security} onChange={e => setForm(p => ({ ...p, security: e.target.value }))}
                        className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid="mail-security">
                  <option value="starttls">STARTTLS (587)</option>
                  <option value="ssl">SSL/TLS (465)</option>
                  <option value="none">Keine</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">SMTP-Benutzer</Label>
              <Input {...bind('smtpUser')} placeholder="Login-Benutzer (meist = E-Mail)" className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-user" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">{editingId ? 'Neues Passwort (leer = unverändert)' : 'SMTP-Passwort'}</Label>
              <Input type="password" {...bind('smtpPassword')} placeholder={editingId ? 'Nur ändern bei neuem Passwort' : 'Hetzner-Passwort'} className="mt-1 bg-white border-gray-300 text-sm" data-testid="mail-password" />
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-gray-500">HTML-Signatur</Label>
              <SignatureFileUpload
                onLoaded={(html) => setForm(p => ({ ...p, signatureHtml: html }))}
              />
            </div>
            <textarea {...bind('signatureHtml')} rows={6}
                      placeholder="<p>Mit freundlichen Grüßen<br/>Max Mustermann<br/>EES Effiziente Energiespeichersysteme GmbH</p>"
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:border-[#9ec043]"
                      data-testid="mail-signature" />
            <p className="text-[10px] text-gray-400 mt-1">Die Signatur wird unter jeder versendeten E-Mail automatisch angehängt. Du kannst eine fertige <code>.html</code>-Datei über „Signatur-Datei laden" einlesen oder direkt bearbeiten.</p>
            {form.signatureHtml && (
              <details className="mt-2">
                <summary className="text-[10px] text-gray-500 cursor-pointer hover:text-[#9ec043]" data-testid="sig-preview-toggle">Vorschau anzeigen</summary>
                <div className="mt-2 border border-dashed border-gray-200 bg-gray-50 rounded p-3 text-xs"
                     dangerouslySetInnerHTML={{ __html: form.signatureHtml }}
                     data-testid="sig-preview" />
              </details>
            )}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <input type="checkbox" id="is-default-sender" checked={!!form.isDefault}
                   onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))}
                   className="accent-[#9ec043]" data-testid="mail-default" />
            <Label htmlFor="is-default-sender" className="text-xs text-gray-500 cursor-pointer">Als Standard-Absender verwenden</Label>
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={loading} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-save-mail">
              <Save className="w-3.5 h-3.5 mr-1" /> {editingId ? 'Änderungen speichern' : 'Anlegen'}
            </Button>
            <Button variant="outline" onClick={resetForm} className="text-sm">Abbrechen</Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 && !showForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
            <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-1">Noch keine Mailserver-Konfiguration angelegt.</p>
            <p className="text-xs text-gray-400">Lege einen Absender an, um Ticket-Antworten direkt aus dem Dashboard per Hetzner-SMTP zu versenden.</p>
          </div>
        )}

        {items.map(it => (
          <div key={it.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`mail-row-${it.id}`}>
            <div className="p-4 flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-[#9ec043]/10 flex items-center justify-center text-[#9ec043] shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#32373c]">{it.label}</span>
                  {it.isDefault && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9ec043]/10 text-[#9ec043] flex items-center gap-1">
                      <Star className="w-3 h-3" /> Standard
                    </span>
                  )}
                  {it.active === false ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">pausiert</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">aktiv</span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">{it.security}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">{it.senderName} &lt;{it.senderEmail}&gt;</div>
                <div className="text-[10px] text-gray-400 mt-0.5 truncate">{it.smtpHost}:{it.smtpPort} · {it.smtpUser} · {it.hasPassword ? 'Passwort gesetzt' : 'kein Passwort'}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => toggleActive(it)}
                        className={`p-1.5 rounded ${it.active === false ? 'text-gray-400 hover:text-green-600' : 'text-green-600 hover:text-gray-400'}`}
                        title={it.active === false ? 'Aktivieren' : 'Pausieren'} data-testid={`btn-toggle-mail-${it.id}`}>
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setTestOpen(it.id); setTestTo(''); setError(''); setSuccess(''); }}
                        className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" title="Testmail senden" data-testid={`btn-test-mail-${it.id}`}>
                  <Send className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => openEdit(it)} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" title="Bearbeiten" data-testid={`btn-edit-mail-${it.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(it)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Löschen" data-testid={`btn-delete-mail-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {testOpen === it.id && (
              <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-gray-500">Empfänger für Testmail</Label>
                  <Input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                         placeholder="test@example.com" className="mt-1 bg-white border-gray-300 text-sm"
                         data-testid="mail-test-to" />
                </div>
                <Button onClick={() => sendTest(it.id)} disabled={loading || !testTo}
                        className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-send-test">
                  <Send className="w-3.5 h-3.5 mr-1" /> Senden
                </Button>
                <Button variant="outline" onClick={() => { setTestOpen(null); setTestTo(''); }} className="text-sm">Abbrechen</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
